const IMPORT_MODES = new Set(["IMPORT", "IMPORT-EVENT"]);
const AIR_EXPORT_MODES = new Set(["EXPORT", "EXPORT-EVENT"]);

export const isCompletedShipmentStatus = (status) =>
  status === "Delivered" || status === "Turnover";

export const isAirExportDoorOrPortToPort = (shipment) =>
  shipment?.shipment_type === "AIR" &&
  AIR_EXPORT_MODES.has(shipment?.shipment_mode) &&
  (shipment?.delivery_mode === "DOOR-PORT" ||
    shipment?.delivery_mode === "PORT-PORT");

export const supportsDynamicCompletionStatus = (shipment) => {
  const isSeaImportPortDoor =
    shipment?.shipment_type === "SEA" &&
    IMPORT_MODES.has(shipment?.shipment_mode) &&
    shipment?.delivery_mode === "PORT-DOOR";

  const isBrokerageSeaImport =
    shipment?.shipment_type === "BROKERAGE" &&
    shipment?.shipment_transport === "SEA" &&
    (!shipment?.shipment_mode || IMPORT_MODES.has(shipment?.shipment_mode)) &&
    (!shipment?.delivery_mode || shipment?.delivery_mode === "PORT-DOOR");

  return isSeaImportPortDoor || isBrokerageSeaImport;
};

const getLatestCompletionRemarkStatus = (shipment) => {
  const remarks = Array.isArray(shipment?.remarks) ? shipment.remarks : [];

  const completionRemark = remarks
    .filter((remark) => isCompletedShipmentStatus(remark?.status))
    .sort((left, right) => new Date(right.date) - new Date(left.date))[0];

  return completionRemark?.status;
};

export const getShipmentDisplayStatus = (shipment) => {
  const completionStatus = getLatestCompletionRemarkStatus(shipment);

  if (
    completionStatus &&
    !isCompletedShipmentStatus(shipment?.status)
  ) {
    return completionStatus;
  }

  return shipment?.status || completionStatus || "";
};

const getResolvedCompletionStatus = (shipment, statusOverride) => {
  if (statusOverride === "Turnover" || statusOverride === "Delivered") {
    return statusOverride;
  }

  if (shipment?.status === "Turnover" || shipment?.status === "Delivered") {
    return shipment.status;
  }

  return getLatestCompletionRemarkStatus(shipment) === "Turnover"
    ? "Turnover"
    : "Delivered";
};

export const getShipmentProgressSteps = (shipment, { statusOverride } = {}) => {
  if (!shipment) {
    return [];
  }

  const completionStatus = supportsDynamicCompletionStatus(shipment)
    ? getResolvedCompletionStatus(shipment, statusOverride)
    : "Delivered";

  if (shipment.shipment_type === "BROKERAGE") {
    return [
      "Order Placed",
      "In Transit",
      "Arrival at Destination",
      "Custom Clearance",
      completionStatus,
    ];
  }

  if (shipment.delivery_mode === "DOOR-DOOR") {
    return [
      "Order Placed",
      "In Warehouse",
      "Departure at Origin",
      "In Transit",
      "Arrival at Destination",
      "Custom Clearance",
      "Delivered",
    ];
  }

  if (shipment.delivery_mode === "DOOR-PORT") {
    if (isAirExportDoorOrPortToPort(shipment)) {
      return [
        "Order Placed",
        "In Warehouse",
        "Departure at Origin",
        "In Transit",
        "Arrival at Destination",
      ];
    }

    return [
      "Order Placed",
      "In Warehouse",
      "Departure at Origin",
      "In Transit",
      "Arrival at Destination",
      "Notification",
      "Turnover",
    ];
  }

  if (shipment.delivery_mode === "PORT-PORT") {
    if (isAirExportDoorOrPortToPort(shipment)) {
      return [
        "Order Placed",
        "Departure at Origin",
        "In Transit",
        "Arrival at Destination",
      ];
    }

    return [
      "Order Placed",
      "Departure at Origin",
      "In Transit",
      "Arrival at Destination",
      "Notification",
      "Turnover",
    ];
  }

  if (shipment.delivery_mode === "PORT-DOOR") {
    return [
      "Order Placed",
      "Departure at Origin",
      "In Transit",
      "Arrival at Destination",
      "Custom Clearance",
      completionStatus,
    ];
  }

  return [
    "Order Placed",
    "In Warehouse",
    "Departure at Origin",
    "In Transit",
    "Arrival at Destination",
    "Custom Clearance",
    "Delivered",
  ];
};

export const getEditableShipmentStatuses = (shipment) => {
  const defaultSteps = getShipmentProgressSteps(shipment, {
    statusOverride: "Delivered",
  });

  if (!supportsDynamicCompletionStatus(shipment)) {
    return defaultSteps;
  }

  return [...defaultSteps.slice(0, -1), "Delivered", "Turnover"];
};

export const getShipmentProgressStepIndex = (shipment, statusOverride) => {
  const steps = getShipmentProgressSteps(shipment, { statusOverride });
  const stepIndex = steps.findIndex((step) => step === statusOverride);

  if (stepIndex >= 0) {
    return stepIndex + 1;
  }

  return shipment?.currentstep || 1;
};

export const getShipmentCurrentProgressStep = (shipment) =>
  getShipmentProgressStepIndex(shipment, getShipmentDisplayStatus(shipment));

export const getShipmentStepIconClass = (stepLabel, shipment) => {
  switch (stepLabel) {
    case "Order Placed":
      return "fas fa-store";
    case "In Warehouse":
      return "fas fa-warehouse";
    case "Departure at Origin":
      if (
        shipment?.shipment_type === "AIR" ||
        shipment?.shipment_transport === "AIR"
      ) {
        return "fas fa-plane-departure";
      }
      if (
        shipment?.shipment_type === "SEA" ||
        shipment?.shipment_transport === "SEA" ||
        shipment?.shipment_type === "BROKERAGE"
      ) {
        return "fas fa-ship";
      }
      return "fas fa-truck-fast";
    case "In Transit":
      if (
        shipment?.shipment_type === "AIR" ||
        shipment?.shipment_transport === "AIR"
      ) {
        return "fas fa-plane";
      }
      if (
        shipment?.shipment_type === "SEA" ||
        shipment?.shipment_transport === "SEA" ||
        shipment?.shipment_type === "BROKERAGE"
      ) {
        return "fas fa-ship";
      }
      return "fas fa-truck";
    case "Arrival at Destination":
      return "fas fa-flag";
    case "Custom Clearance":
      return "fas fa-shield-alt";
    case "Notification":
      return "fas fa-bell";
    case "Delivered":
      return "fas fa-check";
    case "Turnover":
      return "fas fa-handshake";
    default:
      return "fas fa-circle";
  }
};
